// src/pages/ClientsPage.js
import { useState, useEffect } from 'react';
import api from '../utils/api';
import { CLIENT_TYPES, getLabelFor } from '../utils/constants';

export default function ClientsPage() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name:'', type:'mine_operator', province:'', country:'Canada', website:'', notes:'' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    api.get('/clients').then(setClients).finally(() => setLoading(false));
  }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true); setError('');
    try {
      const created = await api.post('/clients', form);
      setClients(c => [created, ...c]);
      setShowForm(false);
      setForm({ name:'', type:'mine_operator', province:'', country:'Canada', website:'', notes:'' });
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const filtered = clients.filter(c =>
    !search || c.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Clients</div>
          <div style={{ fontSize:'0.85rem', color:'var(--text-muted)', marginTop:'0.2rem' }}>{clients.length} organizations</div>
        </div>
        <button className="btn btn-primary" onClick={() => setShowForm(true)}>
          <PlusIcon /> Add Client
        </button>
      </div>

      <input
        type="text" placeholder="Search clients…"
        value={search} onChange={e => setSearch(e.target.value)}
        style={{ maxWidth:320, marginBottom:'1rem' }}
      />

      <div className="card" style={{ padding:0 }}>
        {loading ? (
          <div style={{ padding:'3rem', textAlign:'center', color:'var(--text-muted)' }}>Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="empty-state">
            <BuildingIcon />
            <h3>No clients yet</h3>
            <p style={{ fontSize:'0.85rem' }}>Add clients to associate them with tenders.</p>
            <button className="btn btn-primary" onClick={() => setShowForm(true)}>Add Client</button>
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Type</th>
                <th>Location</th>
                <th>Website</th>
                <th>Tenders</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(c => (
                <tr key={c.id}>
                  <td>
                    <div style={{ fontWeight:600 }}>{c.name}</div>
                    {c.notes && <div style={{ fontSize:'0.75rem', color:'var(--text-muted)', marginTop:'0.15rem' }}>{c.notes.slice(0,80)}{c.notes.length>80?'…':''}</div>}
                  </td>
                  <td style={{ fontSize:'0.82rem', color:'var(--text-secondary)' }}>
                    {getLabelFor(CLIENT_TYPES, c.type)}
                  </td>
                  <td style={{ fontSize:'0.82rem', color:'var(--text-secondary)' }}>
                    {[c.province, c.country].filter(Boolean).join(', ') || '—'}
                  </td>
                  <td>
                    {c.website ? (
                      <a href={c.website} target="_blank" rel="noreferrer" style={{ fontSize:'0.82rem' }}>↗</a>
                    ) : '—'}
                  </td>
                  <td style={{ fontSize:'0.875rem', fontWeight:600, color: c.tender_count > 0 ? 'var(--accent)' : 'var(--text-muted)' }}>
                    {c.tender_count}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showForm && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowForm(false)}>
          <div className="modal">
            <div className="modal-header">
              <span className="modal-title">Add Client</span>
              <button className="btn btn-ghost" onClick={() => setShowForm(false)}>✕</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body" style={{ display:'flex', flexDirection:'column', gap:'1rem' }}>
                {error && <div style={{ background:'var(--red-dim)', border:'1px solid var(--red)', borderRadius:'var(--radius-sm)', padding:'0.75rem', fontSize:'0.85rem', color:'var(--red)' }}>{error}</div>}
                <div className="form-group">
                  <label className="form-label">Company Name *</label>
                  <input value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Cameco Corporation" required />
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'1rem' }}>
                  <div className="form-group">
                    <label className="form-label">Type</label>
                    <select value={form.type} onChange={e => set('type', e.target.value)}>
                      {CLIENT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Province</label>
                    <input value={form.province} onChange={e => set('province', e.target.value)} placeholder="Saskatchewan" />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Website</label>
                  <input type="url" value={form.website} onChange={e => set('website', e.target.value)} placeholder="https://…" />
                </div>
                <div className="form-group">
                  <label className="form-label">Notes</label>
                  <textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={2} placeholder="Key contacts, relationship notes…" />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Add Client'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function PlusIcon()     { return <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>; }
function BuildingIcon() { return <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="2" y="7" width="20" height="14"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/></svg>; }
